import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useForm, Controller, FormProvider, useWatch } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useWorkflow } from '@/context/workflow-context-simple';
import { useWorkflowApi } from '@/hooks/use-workflow-api';
import { useStageFacets } from '@/hooks/use-stage-facets';
import { useToast } from '@/hooks/use-toast';
import { getFacetKeysFromConfiguration } from '@/utils/column-type-helpers';
import { evaluateCondition } from '@/lib/external-source-utils';
import { buildConditionContext, buildSentIdToLocalIdMap, type ActionUserContext } from '@/lib/action-utils';
import type { WorkflowItem } from '@/types/workflow';
import { useAuth } from '@/contexts/auth-context';
import { useEntityType } from '@/contexts/entity-type-context';
import { useVmsApi } from '@/hooks/vms/use-vms-api';

interface MoveToFormData {
  selectedStatus: string | null;
  notes: string;
}

interface MoveToModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItems: string[];
  configuration: any;
  currentStage: string;
  refreshData: (customFilterArray?: string[], orderBy?: string, facetKeys?: string[]) => void;
  setSelectedItems: (items: string[]) => void;
  language: string;
  t: (key: string) => string;
  currentOrderBy?: string;
}

export const MoveToModal: React.FC<MoveToModalProps> = ({ isOpen, onClose, selectedItems, configuration, currentStage, refreshData, setSelectedItems, language, t, currentOrderBy }) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { stageFilters, globalFilters, removeWorkOrdersBulk, workOrders } = useWorkflow();
  const { performStageTransition, bulkUpdateReports } = useWorkflowApi();
  const { refreshStageCounts } = useStageFacets();
  const { toast } = useToast();

  // Tracks pending refresh timers so a new action cancels the previous ones
  const refreshTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])

  // Clear all pending refresh timers on unmount to prevent state updates on a dead component
  useEffect(() => {
    return () => {
      refreshTimersRef.current.forEach(clearTimeout)
      refreshTimersRef.current = []
    }
  }, [])

  const { isInternalUser, userProfileTypeId } = useAuth();
  const { entityType } = useEntityType();
  const { bulkDeleteEmails } = useVmsApi();

  const methods = useForm<MoveToFormData>({
    defaultValues: {
      selectedStatus: null,
      notes: ''
    }
  });

  const { control, formState, getValues, setValue, reset } = methods;
  const selectedStatus = useWatch({ control, name: 'selectedStatus' });

  // Get common available actions that are valid for ALL selected items
  const getCommonAvailableActions = useCallback(() => {
    if (!configuration?.actionConfigurations || selectedItems.length === 0) {
      return [];
    }

    // Get full items from workOrders
    const selectedWorkOrders = selectedItems
      .map(id => workOrders.find((wo: any) => wo.id === id))
      .filter((item): item is WorkflowItem => item !== undefined);

    if (selectedWorkOrders.length === 0) return [];

    // Filter to actions that allow bulk operations. Two kinds qualify:
    //   - stage_transition + allowBulk → routed through performStageTransition
    //     (the original WorkOrder bulk-action path).
    //   - custom_modal + allowBulk → dispatched per actionCode below.
    // Today the only custom_modal bulk action is VmsEmail's Delete; future
    // additions (e.g. bulk MarkAsSpam, bulk Reprocess) plug in by registering
    // their actionCode in the handleConfirm branch.
    const stageTransitionActions = configuration.actionConfigurations.filter(
      (action: any) =>
        (action.actionType === 'stage_transition' && action.allowBulk)
        || (action.actionType === 'custom_modal' && action.allowBulk)
    );

    const userContext: ActionUserContext = { userProfileTypeId, isInternalUser };

    // Only show actions that are valid for ALL selected items
    return stageTransitionActions.filter((action: any) => {
      if (action.condition) {
        return selectedWorkOrders.every(item => {
          const context = buildConditionContext(item, currentStage as any, userContext);
          return evaluateCondition(action.condition, context);
        });
      }

      return true;
    });
  }, [configuration, selectedItems, workOrders, currentStage, userProfileTypeId, isInternalUser]);

  const options = getCommonAvailableActions();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      reset({ selectedStatus: null, notes: '' });
      setIsConfirmOpen(false);
      setIsSubmitting(false);
    }
  }, [isOpen, reset]);

  const handleMove = () => {
    const { selectedStatus } = getValues();
    if (!selectedStatus) return;

    // Find the selected action
    const action = options.find((o: any) => o.actionCode === selectedStatus);
    if (!action) return;

    // Show confirmation modal if action requires confirmation or notes
    if (action.requiresConfirmation || action.allowNotes) {
    setIsConfirmOpen(true);
    } else {
      // Execute immediately for actions that don't require confirmation
      handleConfirm();
    }
  };

  const handleConfirm = async () => {
    const { selectedStatus, notes } = getValues();
    setIsSubmitting(true);

    try {
      // Find the action configuration by actionCode
      const action = configuration?.actionConfigurations?.find(
        (a: any) => a.actionCode === selectedStatus
      );

      if (!action) {
        throw new Error('Action configuration not found for selected action');
      }

      // Get full items for the selected IDs (needed to determine currentWorkflowHubReportId)
      const fullItems = selectedItems
        .map(id => workOrders.find((wo: any) => wo.id === id))
        .filter((item): item is WorkflowItem => item !== undefined);

      // Build a reverse map from the ID the API will receive (currentWorkflowHubReportId for
      // Assignment documents) back to the item's own local state id, so we can remove the
      // correct rows after the API echoes those IDs back in results[].itemId.
      const sentIdToLocalId = buildSentIdToLocalIdMap(fullItems);

      // Dispatch by action type. Stage transitions go through the unified
      // /stage-transitions endpoint; custom_modal bulk actions (today: only
      // VmsEmail's Delete) call their own API and we adapt their result
      // shape onto the same { successCount, failedCount, results[] } envelope
      // the rest of this handler consumes.
      let result: {
        successCount: number
        failedCount: number
        results: Array<{ itemId: string; success: boolean; errorMessage?: string | null }>
      }
      if (action.actionType === 'custom_modal' && action.actionCode === 'Delete' && entityType === 'VmsEmail') {
        const ids = fullItems.map((it: WorkflowItem) => it.id || '').filter(Boolean) as string[];
        const apiResult = await bulkDeleteEmails(ids);
        // Adapt to the stage-transition response shape: per-row outcomes mapped
        // from { id, success, error } → { itemId, success, errorMessage }.
        result = {
          successCount: apiResult?.deletedCount ?? 0,
          failedCount: apiResult?.failedCount ?? 0,
          results: (apiResult?.results ?? []).map((r) => ({
            itemId: r.id,
            success: r.success,
            errorMessage: r.error ?? undefined,
          })),
        };
      } else if (action.actionType === 'custom_modal' && entityType === 'AtsPlacementRequest') {
        // ATS lifecycle actions (Hold / Cancel) are custom_modal actions owned by AtsService, not
        // the shared /stage-transitions endpoint (which has no ATS branch and would 404). Issue
        // them as a bulk Status update with the operator note as Comments — AtsService's bulk path
        // runs each record through PatchAsync, which applies the lifecycle side-effects AND writes
        // the note into the audit row (StatusReason).
        //
        // Derive the target status from the action CODE, not action.targetStage: these are
        // custom_modal actions and targetStage isn't reliably populated on the FE config for them
        // (when it's missing, `Status: undefined` gets dropped by JSON.stringify and the bulk
        // request carries only Comments → no status change). The ATS status enum spells it
        // ATS now shares the canonical vocabulary with ClientRequest / VMS ('Canceled', one l) — the
        // old double-l 'Cancelled' only still worked because AtsPlacementRequestStatuses.Normalize
        // folds it on read, so writing it would start returning InvalidPatch the moment that legacy
        // tolerance is dropped after the Phase 7 migration. WRITE canonical, tolerate legacy on read.
        // Fall back to targetStage for any future custom_modal action not in this map — the shared
        // action config already carries the canonical TargetStage, so no remap is needed.
        const ATS_STATUS_BY_ACTION: Record<string, string> = { Hold: 'OnHold', Cancel: 'Canceled' };
        const targetStage = action.targetStage ?? action.TargetStage;
        const atsStatus = ATS_STATUS_BY_ACTION[action.actionCode] ?? targetStage;
        if (!atsStatus) {
          throw new Error(`Cannot resolve ATS status for action '${action.actionCode}'`);
        }
        const apiResult = await bulkUpdateReports(
          fullItems.map((it: WorkflowItem) => ({ item: it, updates: { Status: atsStatus, Comments: notes || '' } }))
        );
        const successCount = apiResult?.successUpdateCount ?? apiResult?.SuccessUpdateCount ?? 0;
        const failedCount = apiResult?.failedUpdateCount ?? apiResult?.FailedUpdateCount ?? 0;
        const apiMessages = (apiResult?.messages ?? apiResult?.Messages ?? []) as string[];
        // The ATS bulk endpoint returns counts + failure messages, not per-id outcomes. Surface the
        // failure messages as failed results (the empty itemId is fine — the success path below
        // falls back to removing selectedItems, and the post-action refresh reconciles state).
        result = {
          successCount,
          failedCount,
          results: apiMessages.map((m) => ({ itemId: '', success: false, errorMessage: m })),
        };
      } else {
        // Call the new stage-transitions endpoint with notes if provided
        result = await performStageTransition(action.actionCode, fullItems, notes || undefined);
      }

      // Handle the response structure
      const { successCount, failedCount, results } = result;
      const successUpdateCount = successCount;
      const failedUpdateCount = failedCount;
      const messages = results.filter((r: any) => !r.success).map((r: any) => r.errorMessage || 'Unknown error');

      // Build filter array once (used for refresh) - moved before conditional blocks.
      // VMS Email / VMS Request docs don't have a Stage column, so the
      // WorkOrder-shaped `Stage eq 'X'` base filter would reject every row
      // on refresh. For those entity families, omit the base filter and let
      // the grid's own state-driven refresh handle reload.
      const stageConfig = configuration?.stageConfigurations?.find((s: any) => s.stageCode.toLowerCase() === currentStage?.toLowerCase());
      const currentStageFilters = stageFilters.filter(f => f.stageId === stageConfig?.stageId);
      const allFilters = [...globalFilters, ...currentStageFilters].map(f => f.filter).filter(Boolean);
      // Shared gw-client-request-index entities (incl. ClientRequest) have no Stage column.
      const isVmsEntity = entityType === 'VmsEmail' || entityType === 'VmsRequest'
        || entityType === 'AtsPlacementRequest' || entityType === 'ClientRequest';
      const baseFilter = isVmsEntity ? '' : `Stage eq '${currentStage}'`;
      const filterArray = isVmsEntity
        ? (allFilters.length > 0 ? [allFilters.join(' and ')] : [])
        : (allFilters.length > 0
            ? [`${baseFilter} and ${allFilters.join(' and ')}`]
            : [baseFilter]);

      const facetKeys = getFacetKeysFromConfiguration(configuration);

      // Prepare toast data but DO NOT show yet; wait until refresh stabilizes
      let toastVariant: 'success' | 'default' | 'destructive' | undefined;
      let toastTitle: string | undefined;
      let toastDescription: string | undefined;
      if (failedUpdateCount === 0) {
        toastVariant = 'success';
        toastTitle = t('success');
        toastDescription = t('moveToSuccess').replace('{count}', String(successUpdateCount));
      } else if (successUpdateCount === 0) {
        toastVariant = 'destructive';
        const errorDetails = messages && messages.length > 0
          ? `:\n${messages.map((msg: string, index: number) => `${index + 1}. ${msg}`).join('\n')}`
          : '';
        toastTitle = t('error');
        toastDescription = t('moveToFailed')
          .replace('{count}', String(failedUpdateCount))
          .replace('{details}', errorDetails);
      } else {
        toastVariant = 'default';
        const errorDetails = messages && messages.length > 0
          ? `:\n${messages.map((msg: string, index: number) => `${index + 1}. ${msg}`).join('\n')}`
          : '';
        toastTitle = t('partialSuccess');
        toastDescription = t('moveToPartial')
          .replace('{success}', String(successUpdateCount))
          .replace('{failed}', String(failedUpdateCount))
          .replace('{details}', errorDetails);
      }

      // Only continue if there were any successful updates
      if (successUpdateCount > 0) {
        // Optimistically remove successfully moved items from local state immediately
        // V2: Extract succeeded IDs from results array
        const succeededIdsFromApi = results
          .filter((r: any) => r.success)
          .map((r: any) => r.itemId);
        const idsToRemove = succeededIdsFromApi.length > 0
          ? succeededIdsFromApi.map((id: string) => sentIdToLocalId.get(String(id)) ?? id).filter(Boolean)
          : selectedItems;
        removeWorkOrdersBulk(idsToRemove);

        setIsConfirmOpen(false);
        setIsSubmitting(false);
        reset({ selectedStatus: null, notes: '' });
        setSelectedItems([]);
        onClose();

        // Show processing toast immediately; update it in-place once the refresh fires
        // so the success message and stage count update land at the same time.
        const processingDescription = idsToRemove.length === 1
          ? (t('processingActionSingle') || 'Applying your changes, this may take a moment...')
          : (t('processingActionMultiple')?.replace('{count}', String(idsToRemove.length)) ||
             `Applying changes to ${idsToRemove.length} items, this may take a moment...`);
        const { id: processingToastId, update: updateProcessingToast, dismiss: dismissProcessingToast } = toast({
          variant: 'default',
          title: t('processing') || 'Processing...',
          description: processingDescription,
        });

        // Cancel any pending refresh timers from previous actions
        refreshTimersRef.current.forEach(clearTimeout)
        refreshTimersRef.current = [
          // First shot: refresh data + stage counts + transition toast to final result — all at once
          setTimeout(() => {
            refreshData(filterArray, currentOrderBy, facetKeys);
            refreshStageCounts();
            if (toastVariant && toastTitle && toastDescription) {
              updateProcessingToast({ id: processingToastId, variant: toastVariant, title: toastTitle, description: toastDescription });
            }
          }, 5000),
          // Auto-dismiss at 12s
          setTimeout(() => { dismissProcessingToast(); }, 12000),
          // Safety net for slow index updates (stage counts only — lightweight)
          setTimeout(() => { refreshStageCounts(); }, 18000),
        ]
      } else {
        // Keep modal open for all failed updates so user can retry
        setIsConfirmOpen(false);
        setIsSubmitting(false);
        setValue('notes', '');

        // Show failure/partial toast
        if (toastVariant && toastTitle && toastDescription) {
          toast({ variant: toastVariant, title: toastTitle, description: toastDescription });
        }

        // Refresh to show correct state after failure
        refreshTimersRef.current.forEach(clearTimeout)
        refreshTimersRef.current = [
          setTimeout(() => { refreshData(filterArray, currentOrderBy, facetKeys); refreshStageCounts(); }, 1000),
        ]
      }
    } catch (error: any) {
      // Close modals and show error toast
      setIsConfirmOpen(false);
      setIsSubmitting(false);
      reset({ selectedStatus: null, notes: '' });

      toast({
        variant: "destructive",
        title: t('error'),
        description: error?.message || t('moveToError'),
      });

      // On error, refresh data after a delay to restore correct state
      const stageConfig = configuration?.stageConfigurations?.find((s: any) => s.stageCode.toLowerCase() === currentStage?.toLowerCase());
      const currentStageFilters = stageFilters.filter(f => f.stageId === stageConfig?.stageId);
      const allFilters = [...globalFilters, ...currentStageFilters].map(f => f.filter).filter(Boolean);
      const baseFilter = `Stage eq '${currentStage}'`;
      const filterArray = allFilters.length > 0
        ? [`${baseFilter} and ${allFilters.join(' and ')}`]
        : [baseFilter];

      const facetKeys = getFacetKeysFromConfiguration(configuration);

      // Refresh to restore correct state after error
      refreshTimersRef.current.forEach(clearTimeout)
      refreshTimersRef.current = [
        setTimeout(() => { refreshData(filterArray, currentOrderBy, facetKeys); refreshStageCounts(); }, 1000),
      ]
    }
  };

  return (
    <FormProvider {...methods}>
      <Dialog open={isOpen} onOpenChange={open => { if (!open && !isSubmitting) onClose(); }}>
        <DialogContent className="w-[95vw] sm:w-[90vw] max-w-[425px] min-w-[300px] sm:min-w-[350px]">
          {/* Loading overlay */}
          {isSubmitting && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-3 border-blue-500 border-t-transparent"></div>
                <span className="text-gray-700 font-medium">{t('processing')}</span>
              </div>
            </div>
          )}

          <DialogHeader>
            <DialogTitle>{t('bulkAction')}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {options.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">
                  {t('noActionsAvailable')}
                </p>
                <p className="text-xs mt-2 text-gray-400">
                  {t('noActionsHint')}
                </p>
              </div>
            ) : (
              <Controller
                name="selectedStatus"
                control={control}
                render={({ field }) => (
            <div className="grid gap-2">
              {options.map((option: any) => (
                      <div key={option.actionCode} className="flex items-center space-x-2">
                  <input
                    type="radio"
                          id={option.actionCode}
                    name="moveTo"
                          value={option.actionCode}
                          checked={field.value === option.actionCode}
                          onChange={() => field.onChange(option.actionCode)}
                    className="mr-2"
                          disabled={isSubmitting}
                  />
                        <label htmlFor={option.actionCode} className={`text-sm ${isSubmitting ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          {language === 'fr' ? option.titleFrench : option.titleEnglish || option.actionCode}
                  </label>
                </div>
              ))}
            </div>
                )}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="elliptical-outline" onClick={onClose} disabled={isSubmitting}>{t('cancel')}</Button>
            <Button variant="elliptical" onClick={handleMove} disabled={!selectedStatus || isSubmitting || options.length === 0}>{t('ok')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation/Notes Modal */}
      <Dialog open={isConfirmOpen} onOpenChange={(open) => {
        if (!open && !isSubmitting) {
          setIsConfirmOpen(false);
          setValue('notes', '');
        }
      }}>
        <DialogContent className="max-w-lg">
          {/* Loading overlay for confirmation modal */}
          {isSubmitting && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-3 border-blue-500 border-t-transparent"></div>
                <span className="text-gray-700 font-medium">{t('processing')}</span>
              </div>
            </div>
          )}

          <DialogHeader>
            <DialogTitle className="text-blue-900 font-semibold text-base">
              {selectedItems.length === 1
                ? workOrders.find((wo: any) => wo.id === selectedItems[0])?.workerFullName || t('worker')
                : `${selectedItems.length} ${t('selectedItems')}`
              }
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">
                {t('confirmAction')}: {options.find((o: any) => o.actionCode === getValues('selectedStatus'))?.[language === 'fr' ? 'titleFrench' : 'titleEnglish'] || getValues('selectedStatus')}
              </h3>
              <p className="text-gray-600 text-sm">
                {t('actionConfirmMessage')}
              </p>
            </div>

            {(() => {
              const action = options.find((o: any) => o.actionCode === getValues('selectedStatus'));
              return action?.allowNotes && (
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">
                        {t('notes')} ({t('optional')})
                      </label>
                      <Textarea
                        {...field}
                        placeholder={t('addNotesPlaceholder')}
                        rows={4}
                        disabled={isSubmitting}
                        className="resize-none"
                      />
                    </div>
                  )}
                />
              );
            })()}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="elliptical-outline"
              onClick={() => {
                if (!isSubmitting) {
                  setIsConfirmOpen(false);
                  setValue('notes', '');
                }
              }}
              disabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="elliptical"
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('processing') : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
};